import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Search, Factory, Package, MapPin, ArrowRight, Printer, Download, AlertTriangle, Box, Scan, Sparkles, List, Utensils } from "lucide-react";
import { toast } from "sonner";
import { fmtDate, fmtDateTime, fmtNum, movementLabel, dlcStatus, dlcBadgeClass } from "../lib/format";
import LotJourney from "../components/LotJourney";

const MovementIcon = ({ type }) => {
  if (type === "IN") return <Badge variant="outline" className="bg-[#E8EEE9] border-[#1F4E3D]/30 text-[#1F4E3D]">Entrée</Badge>;
  if (type === "OUT") return <Badge variant="outline" className="bg-[#FCEEEA] border-[#C05C47]/30 text-[#C05C47]">Sortie</Badge>;
  if (type === "ADJUST") return <Badge variant="outline" className="bg-[#FBF4E9] border-[#8C6B2F]/30 text-[#8C6B2F]">Ajust.</Badge>;
  if (type === "TRANSFER") return <Badge variant="outline" className="bg-[#EEF2F7] border-[#3B5B7A]/30 text-[#3B5B7A]">Transfert</Badge>;
  return <Badge variant="outline">{type}</Badge>;
};

export default function Traceability() {
  const [params, setParams] = useSearchParams();
  const [lotInput, setLotInput] = useState(params.get("lot") || "");
  const [suggestions, setSuggestions] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [view, setView] = useState("visual"); // 'visual' | 'table'

  // Phase 101dy — Recherche par produit
  const [productInput, setProductInput] = useState("");
  const [productList, setProductList] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [productLots, setProductLots] = useState(null);
  const [includeEmpty, setIncludeEmpty] = useState(false);
  const [productLotsLoading, setProductLotsLoading] = useState(false);

  const loadSuggestions = async () => {
    try {
      const { data } = await api.get("/lots");
      const unique = Array.from(new Set(data.map((l) => l.lot_number).filter(Boolean)));
      setSuggestions(unique);
    } catch {}
  };

  const loadProducts = async () => {
    try {
      const { data } = await api.get("/products");
      setProductList(data || []);
    } catch {}
  };

  useEffect(() => {
    loadSuggestions();
    loadProducts();
    const initial = params.get("lot");
    if (initial) search(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const search = async (lotNumber) => {
    const q = (lotNumber || lotInput).trim();
    if (!q) { toast.error("Saisissez un numéro de lot"); return; }
    setLoading(true);
    setNotFound(false);
    setData(null);
    try {
      const { data } = await api.get(`/traceability/${encodeURIComponent(q)}`);
      setData(data);
      setParams({ lot: q });
    } catch (e) {
      if (e.response?.status === 404) {
        setNotFound(true);
      } else {
        toast.error("Erreur lors de la recherche");
      }
    } finally {
      setLoading(false);
    }
  };

  // Phase 101dy — Recherche des lots d'un produit
  const searchByProduct = async (productId, empty = includeEmpty) => {
    const pid = productId || selectedProductId;
    if (!pid) { toast.error("Sélectionnez un plat"); return; }
    setProductLotsLoading(true);
    setProductLots(null);
    try {
      const { data } = await api.get(`/traceability/by-product/${pid}`, {
        params: { include_empty: empty },
      });
      setProductLots(data);
      if (!data.lots?.length) {
        toast.info(empty ? "Aucun lot pour ce plat" : "Aucun lot avec stock — cochez « Inclure lots épuisés » pour voir l'historique");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur recherche par plat");
    } finally {
      setProductLotsLoading(false);
    }
  };

  const openLotFromProduct = (lotNumber) => {
    if (!lotNumber) return;
    setLotInput(lotNumber);
    // Scroll vers le haut pour voir le résultat détaillé
    window.scrollTo({ top: 0, behavior: "smooth" });
    search(lotNumber);
  };

  const distMap = useMemo(() => {
    if (!data) return {};
    return (data.distributors || []).reduce((acc, d) => { acc[d.id] = d; return acc; }, {});
  }, [data]);

  const depotName = (id) => distMap[id]?.name || (id ? "Dépôt inconnu" : "Non assigné");
  const depotCode = (id) => distMap[id]?.code || "";

  return (
    <div className="space-y-6 max-w-6xl" data-testid="traceability-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#575A57] flex items-center gap-2">
          <Scan className="w-3.5 h-3.5" /> Traçabilité HACCP
        </div>
        <h1 className="font-heading text-2xl font-medium text-[#1A1C1A] mt-0.5">Rechercher un lot</h1>
        <p className="text-sm text-[#575A57] mt-1">
          Deux entrées possibles : par n° de lot (si vous l'avez sous la main) ou par nom de plat.
        </p>
      </div>

      {/* Recherche par n° de lot */}
      <div className="bg-white border border-[#E5E5E0] rounded-lg p-4">
        <div className="text-xs uppercase tracking-wider font-semibold text-[#575A57] mb-2 flex items-center gap-1.5">
          <Package className="w-3.5 h-3.5" /> Par numéro de lot
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-[260px] flex items-center gap-2 bg-[#F9F9F6] border border-[#E5E5E0] rounded-md px-3">
            <Search className="w-4 h-4 text-[#575A57]" />
            <Input
              value={lotInput}
              onChange={(e) => setLotInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") search(); }}
              placeholder="Ex: THAI-0225-001"
              className="border-0 focus-visible:ring-0 bg-transparent px-0 font-mono"
              list="lot-suggestions"
              data-testid="lot-input"
            />
            <datalist id="lot-suggestions">
              {suggestions.map((s) => <option key={s} value={s} />)}
            </datalist>
          </div>
          <Button
            onClick={() => search()}
            disabled={loading || !lotInput.trim()}
            className="bg-[#1F4E3D] hover:bg-[#173B2E]"
            data-testid="search-btn"
          >
            <Search className="w-4 h-4 mr-2" /> {loading ? "Recherche…" : "Rechercher"}
          </Button>
        </div>
      </div>

      {/* Phase 101dy — Recherche par nom de plat */}
      <div className="bg-white border border-[#E5E5E0] rounded-lg p-4" data-testid="search-by-product-box">
        <div className="text-xs uppercase tracking-wider font-semibold text-[#575A57] mb-2 flex items-center gap-1.5">
          <Utensils className="w-3.5 h-3.5" /> Par nom de plat
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-[260px] flex items-center gap-2 bg-[#F9F9F6] border border-[#E5E5E0] rounded-md px-3">
            <Search className="w-4 h-4 text-[#575A57]" />
            <Input
              value={productInput}
              onChange={(e) => {
                setProductInput(e.target.value);
                const match = productList.find((p) => p.name === e.target.value);
                setSelectedProductId(match?.id || null);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  const match = productList.find((p) => p.name.toLowerCase() === productInput.trim().toLowerCase());
                  if (match) {
                    setSelectedProductId(match.id);
                    searchByProduct(match.id);
                  } else {
                    toast.error("Plat inconnu — sélectionnez dans la liste");
                  }
                }
              }}
              placeholder="Ex: Pad Thaï"
              className="border-0 focus-visible:ring-0 bg-transparent px-0"
              list="product-suggestions"
              data-testid="product-input"
            />
            <datalist id="product-suggestions">
              {productList.map((p) => <option key={p.id} value={p.name} />)}
            </datalist>
          </div>
          <label className="flex items-center gap-2 text-xs text-[#575A57] cursor-pointer select-none" data-testid="include-empty-toggle">
            <input
              type="checkbox"
              checked={includeEmpty}
              onChange={(e) => {
                setIncludeEmpty(e.target.checked);
                if (selectedProductId) searchByProduct(selectedProductId, e.target.checked);
              }}
              className="accent-[#1F4E3D]"
            />
            Inclure lots épuisés
          </label>
          <Button
            onClick={() => searchByProduct()}
            disabled={productLotsLoading || !selectedProductId}
            className="bg-[#1F4E3D] hover:bg-[#173B2E]"
            data-testid="search-by-product-btn"
          >
            <Search className="w-4 h-4 mr-2" /> {productLotsLoading ? "Recherche…" : "Lister les lots"}
          </Button>
        </div>
      </div>

      {/* Résultats de la recherche par plat */}
      {productLots && productLots.lots?.length > 0 && (
        <div className="bg-white border border-[#E5E5E0] rounded-lg p-6" data-testid="product-lots-result">
          <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
            <div>
              <h2 className="font-heading text-lg font-medium text-[#1A1C1A] flex items-center gap-2">
                <Package className="w-5 h-5 text-[#1F4E3D]" /> Lots de {productLots.product.name}
              </h2>
              <p className="text-xs text-[#575A57] font-mono mt-0.5">
                SKU {productLots.product.sku} · {productLots.total_lots} lot{productLots.total_lots > 1 ? "s" : ""} · Stock total : {fmtNum(productLots.total_quantity, productLots.product.unit || "")}
              </p>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F9F9F6] text-xs uppercase tracking-wider text-[#575A57]">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold">N° Lot</th>
                  <th className="text-left px-3 py-2 font-semibold">Production</th>
                  <th className="text-left px-3 py-2 font-semibold">DLC</th>
                  <th className="text-right px-3 py-2 font-semibold">Stock</th>
                  <th className="text-left px-3 py-2 font-semibold">Emplacement</th>
                  <th className="text-left px-3 py-2 font-semibold">OF</th>
                  <th className="w-8"></th>
                </tr>
              </thead>
              <tbody>
                {productLots.lots.map((l) => {
                  const isEmpty = (l.quantity || 0) <= 0;
                  return (
                    <tr
                      key={l.id}
                      onClick={() => openLotFromProduct(l.lot_number)}
                      className={`border-t border-[#F0F0EB] cursor-pointer hover:bg-[#F9F9F6] transition-colors ${isEmpty ? "opacity-60" : ""}`}
                      data-testid={`product-lot-row-${l.lot_number}`}
                    >
                      <td className="px-3 py-2 font-mono font-semibold text-[#1F4E3D]">{l.lot_number || "—"}</td>
                      <td className="px-3 py-2 font-mono text-xs">{l.production_date ? fmtDate(l.production_date) : "—"}</td>
                      <td className="px-3 py-2">{l.dlc ? <DlcBadge dlc={l.dlc} /> : "—"}</td>
                      <td className="px-3 py-2 text-right font-mono font-semibold">
                        {isEmpty
                          ? <span className="text-[#A3A6A3]">épuisé</span>
                          : fmtNum(l.quantity, productLots.product.unit || "")}
                      </td>
                      <td className="px-3 py-2">
                        <div className="font-medium text-xs">{l.distributor_name || <span className="text-[#A3A6A3]">Non assigné</span>}</div>
                        {l.emplacement_number != null && (
                          <div className="text-xs font-mono text-[#575A57]">E{l.emplacement_number}</div>
                        )}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs text-[#575A57]">{l.order_reference || "—"}</td>
                      <td className="px-3 py-2 text-right">
                        <ArrowRight className="w-4 h-4 text-[#1F4E3D] inline" />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-[#575A57] mt-3">Cliquez sur une ligne pour voir la traçabilité complète du lot.</p>
        </div>
      )}

      {notFound && (
        <div className="bg-white border border-[#E8C1B4] rounded-lg p-6 text-center">
          <AlertTriangle className="w-8 h-8 text-[#C05C47] mx-auto mb-2" />
          <div className="font-heading text-lg text-[#1A1C1A]">Lot introuvable</div>
          <div className="text-sm text-[#575A57] mt-1">
            Aucun lot ne correspond à <span className="font-mono font-semibold">{lotInput}</span>.
          </div>
        </div>
      )}

      {data && (
        <div className="space-y-6" data-testid="traceability-result">
          {/* En-tête produit */}
          <div className="bg-white border border-[#E5E5E0] rounded-lg p-6">
            <div className="flex items-start justify-between flex-wrap gap-4">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-[#575A57] font-semibold">Lot</div>
                <div className="font-mono font-bold text-2xl text-[#1A1C1A] mt-1" data-testid="result-lot-number">
                  {data.lot_number}
                </div>
                {data.product && (
                  <div className="mt-3">
                    <div className="font-heading text-xl font-medium text-[#1A1C1A]">{data.product.name}</div>
                    <div className="text-xs font-mono text-[#575A57]">SKU : {data.product.sku} · Catégorie : {data.product.category}</div>
                  </div>
                )}
              </div>
              <div className="flex flex-col items-end gap-2">
                <div className="bg-[#F9F9F6] border border-[#E5E5E0] rounded-md px-4 py-2.5">
                  <div className="text-xs uppercase tracking-wider text-[#575A57] font-semibold">Stock total restant</div>
                  <div className="font-heading text-2xl font-medium text-[#1F4E3D]">
                    {fmtNum(data.total_quantity, data.product?.unit || "")}
                  </div>
                </div>
                {data.product && (
                  <Link to={`/produits/${data.product.id}/fiche`}>
                    <Button variant="outline" size="sm" data-testid="view-sheet-btn">
                      Fiche technique <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                )}
              </div>
            </div>

            {data.product && (data.product.allergens?.length > 0 || data.product.ingredients) && (
              <div className="mt-4 pt-4 border-t border-[#F0F0EB] space-y-2">
                {data.product.allergens?.length > 0 && (
                  <div className="text-sm">
                    <span className="uppercase tracking-wider text-xs font-bold text-[#8C6B2F]">Allergènes : </span>
                    <span className="text-[#8C6B2F] font-semibold">{data.product.allergens.join(", ")}</span>
                  </div>
                )}
                {data.product.ingredients && (
                  <div className="text-sm leading-snug">
                    <span className="uppercase tracking-wider text-xs font-bold text-[#3A3A3A]">Ingrédients : </span>
                    <span className="text-[#3A3A3A]">{data.product.ingredients}</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Toggle vue */}
          <div className="flex items-center gap-2" data-testid="view-toggle">
            <button
              onClick={() => setView("visual")}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold uppercase tracking-wider transition-all ${
                view === "visual"
                  ? "bg-[#1F4E3D] text-white shadow-sm"
                  : "bg-white border border-[#E5E5E0] text-[#575A57] hover:border-[#1F4E3D]/40"
              }`}
              data-testid="view-visual-btn"
            >
              <Sparkles className="w-3.5 h-3.5" /> Vue visuelle
            </button>
            <button
              onClick={() => setView("table")}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold uppercase tracking-wider transition-all ${
                view === "table"
                  ? "bg-[#1F4E3D] text-white shadow-sm"
                  : "bg-white border border-[#E5E5E0] text-[#575A57] hover:border-[#1F4E3D]/40"
              }`}
              data-testid="view-table-btn"
            >
              <List className="w-3.5 h-3.5" /> Vue détaillée
            </button>
          </div>

          {view === "visual" && <LotJourney data={data} />}

          {view === "table" && <>
          {/* OF d'origine */}
          {data.order && (() => {
            // Trouve la ligne de l'OF correspondant à ce lot_number
            const matchingLine = (data.order.lines || []).find((ln) => ln.lot_number === data.lot_number);
            const lineQty = matchingLine?.quantity_produced || matchingLine?.quantity_target || 0;
            const lineDlc = matchingLine?.dlc;
            const lineCount = (data.order.lines || []).length;
            return (
            <div className="bg-white border border-[#E5E5E0] rounded-lg p-6">
              <h2 className="font-heading text-lg font-medium text-[#1A1C1A] flex items-center gap-2 mb-4">
                <Factory className="w-5 h-5 text-[#1F4E3D]" /> Ordre de fabrication d'origine
                {lineCount > 1 && (
                  <span className="text-xs font-normal text-[#575A57]">
                    · OF multi-produits ({lineCount} lignes)
                  </span>
                )}
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                <div>
                  <div className="text-xs uppercase tracking-wider text-[#575A57] font-semibold">Référence</div>
                  <div className="font-mono font-semibold mt-0.5">{data.order.reference}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-[#575A57] font-semibold">Statut</div>
                  <div className="font-semibold mt-0.5">{data.order.status}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-[#575A57] font-semibold">Qté de cette ligne</div>
                  <div className="font-mono mt-0.5">{fmtNum(lineQty)}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-[#575A57] font-semibold">Production</div>
                  <div className="font-mono mt-0.5">{data.order.completed_at ? fmtDate(data.order.completed_at) : "—"}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-[#575A57] font-semibold">DLC ligne</div>
                  {lineDlc ? (
                    <DlcBadge dlc={lineDlc} />
                  ) : (
                    <div className="mt-0.5">—</div>
                  )}
                </div>
              </div>
            </div>
            );
          })()}

          {/* Localisation actuelle */}
          <div className="bg-white border border-[#E5E5E0] rounded-lg p-6">
            <h2 className="font-heading text-lg font-medium text-[#1A1C1A] flex items-center gap-2 mb-4">
              <MapPin className="w-5 h-5 text-[#1F4E3D]" /> Localisation actuelle ({data.lots.filter((l) => (l.quantity || 0) > 0).length} barquette{data.lots.filter((l) => (l.quantity || 0) > 0).length > 1 ? "s" : ""})
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F9F9F6] text-xs uppercase tracking-wider text-[#575A57]">
                  <tr>
                    <th className="text-left px-3 py-2 font-semibold">Dépôt</th>
                    <th className="text-left px-3 py-2 font-semibold">Emplacement</th>
                    <th className="text-right px-3 py-2 font-semibold">Quantité</th>
                    <th className="text-left px-3 py-2 font-semibold">DLC</th>
                    <th className="text-left px-3 py-2 font-semibold">Prod.</th>
                  </tr>
                </thead>
                <tbody>
                  {data.lots.filter((l) => (l.quantity || 0) > 0).map((l) => (
                    <tr key={l.id} className="border-t border-[#F0F0EB]">
                      <td className="px-3 py-2">
                        <div className="font-medium">{depotName(l.distributor_id)}</div>
                        {depotCode(l.distributor_id) && (
                          <div className="text-xs font-mono text-[#575A57]">{depotCode(l.distributor_id)}</div>
                        )}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">
                        {l.emplacement_number != null
                          ? `E${l.emplacement_number}${l.barquette_index ? `·B${l.barquette_index}` : ""}`
                          : <span className="text-[#A3A6A3]">—</span>}
                      </td>
                      <td className="px-3 py-2 text-right font-mono font-semibold">
                        {fmtNum(l.quantity, data.product?.unit || "")}
                      </td>
                      <td className="px-3 py-2">
                        {l.dlc ? <DlcBadge dlc={l.dlc} /> : "—"}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">{l.production_date ? fmtDate(l.production_date) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mouvements */}
          <div className="bg-white border border-[#E5E5E0] rounded-lg p-6">
            <h2 className="font-heading text-lg font-medium text-[#1A1C1A] flex items-center gap-2 mb-4">
              <Box className="w-5 h-5 text-[#1F4E3D]" /> Mouvements de stock ({data.movements.length})
            </h2>
            {data.movements.length === 0 ? (
              <p className="text-sm text-[#A3A6A3]">Aucun mouvement enregistré.</p>
            ) : (
              <div className="space-y-2">
                {data.movements.map((m) => (
                  <div key={m.id} className="flex items-center gap-3 py-2 border-b border-[#F0F0EB] last:border-0">
                    <div className="shrink-0"><MovementIcon type={m.type} /></div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-[#1A1C1A]">
                        {m.type === "TRANSFER" ? (
                          <>
                            <span className="font-medium">{depotName(m.from_distributor_id)}</span>
                            <ArrowRight className="inline w-3.5 h-3.5 mx-1.5 text-[#575A57]" />
                            <span className="font-medium">{depotName(m.to_distributor_id)}</span>
                          </>
                        ) : (
                          <span className="font-medium">{depotName(m.distributor_id)}</span>
                        )}
                        <span className="ml-2 font-mono text-[#1F4E3D] font-semibold">
                          {m.type === "OUT" ? "−" : m.type === "IN" ? "+" : ""}{fmtNum(m.quantity)}
                        </span>
                      </div>
                      {m.reason && <div className="text-xs text-[#575A57] mt-0.5">{m.reason}</div>}
                    </div>
                    <div className="text-xs font-mono text-[#575A57] shrink-0">{fmtDateTime(m.created_at)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Historique impressions */}
          <div className="bg-white border border-[#E5E5E0] rounded-lg p-6">
            <h2 className="font-heading text-lg font-medium text-[#1A1C1A] flex items-center gap-2 mb-4">
              <Printer className="w-5 h-5 text-[#1F4E3D]" /> Étiquettes imprimées ({data.prints.length})
            </h2>
            {data.prints.length === 0 ? (
              <p className="text-sm text-[#A3A6A3]">Aucune étiquette imprimée pour ce lot.</p>
            ) : (
              <div className="space-y-2">
                {data.prints.map((p) => (
                  <div key={p.id} className="flex items-center gap-3 py-2 border-b border-[#F0F0EB] last:border-0 text-sm">
                    <div className="shrink-0">
                      {p.action === "PDF"
                        ? <Badge variant="outline" className="gap-1 bg-[#E8EEE9] border-[#1F4E3D]/30 text-[#1F4E3D]"><Download className="w-3 h-3" />PDF</Badge>
                        : <Badge variant="outline" className="gap-1 bg-[#F0F0EB] border-[#E5E5E0] text-[#1A1C1A]"><Printer className="w-3 h-3" />Impression</Badge>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium">{p.copies} exemplaire{p.copies > 1 ? "s" : ""}</div>
                      <div className="text-xs text-[#575A57]">
                        {p.format_label}{p.order_reference ? ` · OF ${p.order_reference}` : ""}
                      </div>
                    </div>
                    <div className="text-xs font-mono text-[#575A57] shrink-0">{fmtDateTime(p.created_at)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
          </>}
        </div>
      )}
    </div>
  );
}

const DlcBadge = ({ dlc }) => {
  const status = dlcStatus(dlc);
  if (!status) return <span className="font-mono text-xs">{dlc}</span>;
  return (
    <Badge variant="outline" className={`${dlcBadgeClass(status.kind)} font-mono gap-1`}>
      {fmtDate(dlc)}
      {status.kind === "expired" && <span>({status.days}j)</span>}
      {status.kind === "soon" && <span>(+{status.days}j)</span>}
    </Badge>
  );
};
